import { projectOutboxIntents } from "../../../../runtime/events/outbox.ts";
import type { AthenaIngressIR } from "../../../../runtime/ingress/ir.ts";
import type {
	EventIngressDatabase,
	EventIngressPersistence,
} from "../../../../runtime/ingress/persistence.ts";
import type { CanonicalBillingDocument } from "../../../canonical/document.ts";
import {
	type CanonicalBillingEvent,
	assertCanonicalBillingTransition,
	canonicalizeBillingEvents,
} from "../../../canonical/transition.ts";
import type {
	BillingEventRepository,
	BillingIngressDocumentRepository,
	BillingOutboxRepository,
} from "./repository.ts";
import { assertRevisionIsNotStale } from "./revision.ts";

export interface ApplyCanonicalBillingIngressInput {
	database: EventIngressDatabase;
	ingress: AthenaIngressIR;
	document: CanonicalBillingDocument;
	persistence: EventIngressPersistence;
	documents: BillingIngressDocumentRepository;
	events: BillingEventRepository;
	outbox: BillingOutboxRepository;
	canonicalizeEvents?: typeof canonicalizeBillingEvents;
}

export interface ApplyCanonicalBillingIngressResult {
	duplicate: boolean;
	events: readonly CanonicalBillingEvent[];
	document: CanonicalBillingDocument;
}

export async function applyCanonicalBillingIngress(
	input: ApplyCanonicalBillingIngressInput,
): Promise<ApplyCanonicalBillingIngressResult> {
	return input.database.transaction(async (tx) => {
		const locked = await input.persistence.lock(tx, input.ingress.id);
		if (locked?.status === "processed") {
			return {
				duplicate: true,
				events: [],
				document: input.document,
			};
		}

		const previous = await input.documents.readCurrentForUpdate(
			tx,
			input.document,
			input.ingress.connectionId,
		);
		assertRevisionIsNotStale(previous, input.document);
		assertCanonicalBillingTransition(previous, input.document);
		await input.documents.upsert(
			tx,
			input.document,
			input.ingress.connectionId,
		);

		const canonicalize = input.canonicalizeEvents ?? canonicalizeBillingEvents;
		const events = canonicalize({
			ingress: input.ingress,
			previous,
			current: input.document,
		});
		await input.events.append(tx, events);
		await input.outbox.enqueue(tx, projectOutboxIntents(events));
		await input.persistence.markProcessed(tx, {
			ingressId: input.ingress.id,
			eventIds: events.map((event) => event.id),
		});
		return { duplicate: false, events, document: input.document };
	});
}
